import { OrthographicCamera, Text, useTexture } from '@react-three/drei';
import * as THREE from 'three';
import type { CredentialData, CredentialFaceConfig } from './types';

const AEONIK_BOLD = '/fonts/Aeonik-Bold.ttf';
const AEONIK_REGULAR = '/fonts/Aeonik-Regular.ttf';
const AEONIK_MONO_REGULAR = '/fonts/AeonikMono-Regular.otf';
const OXANIUM_REGULAR = '/fonts/Oxanium-Regular.ttf';
const PH26_LOGO = '/hack-26/credentials/logo-hack-26-white.svg';
const HACKER_WORD = '/hack-26/credentials/hacker-word.png';
const BANANA = '/hack-26/credentials/banana-transparent.png';

useTexture.preload(PH26_LOGO);
useTexture.preload(HACKER_WORD);
useTexture.preload(BANANA);

// Aeonik Mono is monospaced, so how wide a string prints is its length times this many
// ems (a hair over the font's 0.6 advance, to stay on the safe side of the card edge).
const MONO_ADVANCE = 0.62;
const NAME_MAX_WIDTH = 0.82;
const NAME_MAX_SIZE = 0.105;
const NAME_MIN_SIZE = 0.03;
const NAME_MAX_LINES = 2;
const HANDLE_MAX_WIDTH = 0.84;
const HANDLE_MAX_SIZE = 0.052;
const HANDLE_MIN_SIZE = 0.03;

function wrapLineCount(words: string[], charsPerLine: number) {
  let lines = 1;
  let used = 0;
  for (const word of words) {
    if (word.length > charsPerLine) return Number.POSITIVE_INFINITY;
    if (used === 0) used = word.length;
    else if (used + 1 + word.length <= charsPerLine) used += 1 + word.length;
    else {
      lines += 1;
      used = word.length;
    }
  }
  return lines;
}

// The largest font size at which the name wraps into at most two lines inside the card
// (and its longest word fits on one).
// A fixed size let long names run to three or four lines and collide with the handle.
export function fitNameSize(name: string) {
  const words = name.split(/\s+/).filter(Boolean);
  // A word can't be wrapped, so the longest one caps the size on its own.
  const longest = Math.max(1, ...words.map((word) => word.length));
  const widest = NAME_MAX_WIDTH / (longest * MONO_ADVANCE);
  const start = Math.min(NAME_MAX_SIZE, widest);
  for (let size = start; size > NAME_MIN_SIZE; size -= 0.002) {
    const charsPerLine = Math.floor(NAME_MAX_WIDTH / (size * MONO_ADVANCE));
    if (wrapLineCount(words, charsPerLine) <= NAME_MAX_LINES) return size;
  }
  return NAME_MIN_SIZE;
}

// The handle is a single line, so it only has to shrink until it fits the card's width.
export function fitHandleSize(text: string) {
  const size = HANDLE_MAX_WIDTH / (Math.max(text.length, 1) * MONO_ADVANCE);
  return Math.max(HANDLE_MIN_SIZE, Math.min(HANDLE_MAX_SIZE, size));
}

// The 3D face of the badge — one component for the three credential-face-26-*.tsx twins,
// with everything they disagree about arriving as `config` (PORTING.md G5).
export function CredentialFace({
  data,
  config,
}: {
  data: CredentialData;
  config: CredentialFaceConfig;
}) {
  const [logoTexture, logoGroupTexture, hackerWordTexture, bananaTexture] =
    useTexture([PH26_LOGO, config.logoGroupTexture, HACKER_WORD, BANANA]);
  logoTexture.colorSpace = THREE.SRGBColorSpace;
  logoGroupTexture.colorSpace = THREE.SRGBColorSpace;
  hackerWordTexture.colorSpace = THREE.SRGBColorSpace;
  bananaTexture.colorSpace = THREE.SRGBColorSpace;

  const name = data.name.trim().toUpperCase();
  const handle = `@${data.handle.toUpperCase()}`;

  return (
    <>
      <OrthographicCamera
        makeDefault
        position={[0, 0, 5]}
        left={-1}
        right={1}
        top={1}
        bottom={-1}
        near={0.1}
        far={10}
      />
      <ambientLight intensity={1} />

      <group scale={[1, -1, 1]}>
        <mesh position={[0, 0, -0.01]}>
          <planeGeometry args={[2, 2]} />
          <meshBasicMaterial color="#0a0a0a" />
        </mesh>
        <mesh position={[0.5, 0.25, 0]}>
          <planeGeometry args={[1, 1.5]} />
          <meshBasicMaterial color="#ffffff" />
        </mesh>

        {/* ===== FRONT ===== */}
        <group position={[-0.5, 0.25, 0]}>
          <Text
            font={AEONIK_REGULAR}
            position={[-0.44, 0.66, 0.01]}
            fontSize={0.034}
            color="#ffffff"
            anchorX="left"
            letterSpacing={0.08}
          >
            {config.dateRange}
          </Text>
          <Text
            font={OXANIUM_REGULAR}
            position={[0.44, 0.66, 0.01]}
            fontSize={0.046}
            color="#ffffff"
            anchorX="right"
            letterSpacing={-0.02}
          >
            platanus hack [26]
          </Text>

          <mesh position={[0, 0.43, 0.005]}>
            <planeGeometry args={[0.72, 0.16]} />
            <meshBasicMaterial color="#ffffff" />
          </mesh>
          <mesh position={[0, 0.43, 0.01]}>
            <planeGeometry args={[0.62, 0.132]} />
            <meshBasicMaterial map={hackerWordTexture} transparent />
          </mesh>

          <Text
            font={AEONIK_MONO_REGULAR}
            position={[0, -0.05, 0.01]}
            fontSize={fitNameSize(name)}
            color="white"
            anchorX="center"
            anchorY="bottom"
            maxWidth={NAME_MAX_WIDTH}
            textAlign="center"
          >
            {name}
          </Text>
          <Text
            font={AEONIK_MONO_REGULAR}
            position={[0, -0.135, 0.01]}
            fontSize={fitHandleSize(handle)}
            color="#ffffff"
            anchorX="center"
            anchorY="middle"
          >
            {handle}
          </Text>

          <Text
            font={AEONIK_REGULAR}
            position={[0, config.cityY, 0.01]}
            fontSize={0.035}
            color="#ffffff"
            anchorX="center"
            letterSpacing={0.16}
          >
            {config.cityLabel}
          </Text>
          <mesh position={[0, config.logo.y, 0.01]}>
            <planeGeometry args={[config.logo.width, config.logo.height]} />
            <meshBasicMaterial map={logoTexture} transparent />
          </mesh>
          <mesh position={[0, config.logoGroup.y, 0.01]}>
            <planeGeometry
              args={[config.logoGroup.width, config.logoGroup.height]}
            />
            <meshBasicMaterial map={logoGroupTexture} transparent />
          </mesh>
        </group>

        {/* ===== BACK ===== */}
        <group position={[0.5, 0.25, 0]}>
          <mesh position={[0, 0, 0]}>
            <planeGeometry args={[1, 1.5]} />
            <meshBasicMaterial color="#0a0a0a" />
          </mesh>
          <mesh position={[0, 0, 0.01]}>
            <planeGeometry args={[0.62, 0.62]} />
            <meshBasicMaterial map={bananaTexture} transparent />
          </mesh>
        </group>

        {/* ===== EDGE STRIP ===== */}
        <group position={[0, -0.75, 0]}>
          <Text
            font={AEONIK_BOLD}
            position={[0, 0, 0]}
            fontSize={0.18}
            color="#a3e635"
            anchorX="center"
            anchorY="middle"
            letterSpacing={0.05}
          >
            PLATANUS HACK 2026
          </Text>
        </group>
      </group>
    </>
  );
}
